<template>
  <div class="h-auto transition-all duration-300">
    <!-- Filters Bar -->
    <div class="mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
      <!-- Date Filter -->
      <div class="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
        <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <div class="flex-1">
          <label class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Date Filter</label>
          <input type="date" v-model="departmentFilterDate" :disabled="!isSuperAdmin"
            :title="!isSuperAdmin ? 'Date filter is only available for super administrators' : ''"
            class="w-full text-sm border-0 bg-transparent text-gray-900 dark:text-white font-medium focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed p-0"
            @change="fetchDepartmentData" />
        </div>
      </div>

      <!-- Department Filter -->
      <div class="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
        <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white shadow-md">
          <i class="fas fa-building"></i>
        </div>
        <div class="flex-1">
          <label class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Department</label>
          <select v-model="selectedDepartment"
            class="w-full text-sm border-0 bg-transparent text-gray-900 dark:text-white font-medium focus:ring-0 p-0 cursor-pointer"
            @change="fetchDepartmentData">
            <option value="all">All Departments</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
              {{ dept.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Orders Section -->
      <OrdersSection
        :order-details="departmentData.orderDetails"
        :loading="departmentDataLoading"
        :pending-approvals="canApproveOrders ? pendingOrdersCount : 0"
        @open-order="$emit('open-order', $event)"
      />

      <!-- Payments Section -->
      <PaymentsSection
        :payment-details="departmentData.paymentDetails"
        :loading="departmentDataLoading"
        :pending-approvals="canApprovePayments ? pendingPaymentsCount : 0"
        @open-payment="$emit('open-payment', $event)"
      />

      <!-- Expenses Section -->
      <ExpensesSection
        :expense-details="departmentData.expenseDetails"
        :loading="departmentDataLoading"
        :pending-approvals="canApproveExpenses ? pendingExpensesCount : 0"
        @open-expense="$emit('open-expense', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { useAuth } from '@/composables/useAuth';
import { useMasterData } from '@/composables/useMasterData';
import { supabase } from '@/lib/supabase';
import OrdersSection from './department/OrdersSection.vue';
import PaymentsSection from './department/PaymentsSection.vue';
import ExpensesSection from './department/ExpensesSection.vue';

const props = defineProps({
  initialDate: {
    type: String,
    default: () => new Date().toISOString().split('T')[0]
  },
  initialDepartment: {
    type: [String, Number],
    default: 'all'
  }
});

const emit = defineEmits(['update:date', 'update:department', 'open-order', 'open-payment', 'open-expense', 'update:rowCount']);

// Initialize composables
const api = useApi();
const toast = useToast();
const masterData = useMasterData();
const { hasRole, hasPermission } = useAuth();

// Role / permission helpers
const isSuperAdmin = computed(() => hasRole('super_admin'));
const canApproveOrders = computed(
  () => hasPermission('orders.approve') || hasPermission('orders.edit_approval')
);
const canApprovePayments = computed(() => hasPermission('payments.approve'));
const canApproveExpenses = computed(() => hasPermission('expenses.approve'));

// State
const departmentFilterDate = ref(props.initialDate);
console.log('[DepartmentOverview] Initializing with date:', {
  propsInitialDate: props.initialDate,
  currentDate: new Date().toISOString().split('T')[0],
  currentDateRaw: new Date().toString()
});
const selectedDepartment = ref(props.initialDepartment);
const departments = ref([]);
const departmentDataLoading = ref(false);
const departmentData = ref({
  orderDetails: [],
  paymentDetails: [],
  expenseDetails: []
});

// Pending approval counts (global, not per-department)
const pendingOrdersCount = ref(0);
const pendingPaymentsCount = ref(0);
const pendingExpensesCount = ref(0);

onMounted(() => {
  // No real-time subscriptions here - parent component will handle them
  // and call our refresh function
});

// Watch for changes in date/department and emit them
watch(departmentFilterDate, (newVal) => {
  emit('update:date', newVal);
});

watch(selectedDepartment, (newVal) => {
  emit('update:department', newVal);
});

// Fetch departments from cache
const fetchDepartments = async () => {
  try {
    console.log('[Departmental Overview] Loading departments from cache');
    const result = await masterData.loadDepartments();
    departments.value = result || [];
    console.log('[Departmental Overview] Departments loaded:', departments.value);
  } catch (error) {
    console.error('[Departmental Overview] Error loading departments:', error);
    toast.error('Failed to load departments');
  }
};

// Fetch lightweight pending approval counts (only for users with approval permissions)
const fetchPendingApprovalCounts = async () => {
  try {
    // Orders pending approval
    if (canApproveOrders.value) {
      const res = await api.get('/orders/pending');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      pendingOrdersCount.value = list.length || 0;
    } else {
      pendingOrdersCount.value = 0;
    }

    // Payments pending approval
    if (canApprovePayments.value) {
      const res = await api.get('/payments/pending');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      pendingPaymentsCount.value = list.length || 0;
    } else {
      pendingPaymentsCount.value = 0;
    }

    // Expenses pending approval
    if (canApproveExpenses.value) {
      const res = await api.get('/expenses/pending');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      pendingExpensesCount.value = list.length || 0;
    } else {
      pendingExpensesCount.value = 0;
    }
  } catch (error) {
    console.warn('[Departmental Overview] Error fetching pending approval counts:', error);
  }
};

// Fetch departmental data
const fetchDepartmentData = async () => {
  try {
    departmentDataLoading.value = true;
    console.log('[Departmental Overview] Fetching data for:', {
      date: departmentFilterDate.value,
      rawDate: new Date(departmentFilterDate.value).toString(),
      department: selectedDepartment.value,
      departmentName: departments.value.find(d => d.id === selectedDepartment.value)?.name || 'All Departments'
    });

    // Format date to YYYY-MM-DD
    const formattedDate = departmentFilterDate.value ? new Date(departmentFilterDate.value).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    console.log('[Departmental Overview] Formatted date for API request:', formattedDate);

    const response = await api.get('/dashboard/departments', {
      params: {
        date: formattedDate,
        department_id: selectedDepartment.value !== 'all' ? selectedDepartment.value : undefined,
        exclude_statuses: ['cancelled', 'rejected', 'pending']
      }
    });

    if (response?.data) {
      console.log('[Departmental Overview] Raw response:', response.data);

      // Add detailed logging of expense data structure
      console.log('[Departmental Overview] Expense Details Structure:', {
        sampleExpense: response.data.expenseDetails?.[0],
        availableFields: response.data.expenseDetails?.[0] ? Object.keys(response.data.expenseDetails[0]) : [],
        totalExpenses: response.data.expenseDetails?.length || 0
      });

      departmentData.value = {
        orderDetails: response.data.orderDetails || [],
        paymentDetails: response.data.paymentDetails || [],
        expenseDetails: response.data.expenseDetails || []
      };

      // Calculate and emit total row count
      const totalRows = departmentData.value.orderDetails.length + 
                       departmentData.value.paymentDetails.length + 
                       departmentData.value.expenseDetails.length;
      emit('update:rowCount', totalRows);

      console.log('[Departmental Overview] Processed data:', {
        orders: departmentData.value.orderDetails.length,
        payments: departmentData.value.paymentDetails.length,
        expenses: departmentData.value.expenseDetails.length,
        totalRows: totalRows,
        date: formattedDate
      });
    }

    // Update pending approval counts (once per data refresh)
    await fetchPendingApprovalCounts();
  } catch (error) {
    console.error('[Departmental Overview] Error fetching data:', error);
    toast.error('Failed to load department data');
    departmentData.value = { orderDetails: [], paymentDetails: [], expenseDetails: [] };
    // Emit 0 rows on error
    emit('update:rowCount', 0);
  } finally {
    departmentDataLoading.value = false;
  }
};

// Add a watcher for filter changes
watch([selectedDepartment, departmentFilterDate], ([newDept, newDate], [oldDept, oldDate]) => {
  console.log('[Departmental Overview] Filters changed:', {
    department: {
      from: oldDept,
      to: newDept,
      name: departments.value.find(d => d.id === newDept)?.name || 'All Departments'
    },
    date: {
      from: oldDate,
      to: newDate
    }
  });
  // Fetch new data when filters change
  fetchDepartmentData();
}, { immediate: true });

// Fetch departments on component mount
fetchDepartments();

// Expose refresh function to parent component
defineExpose({
  refresh: fetchDepartmentData
});
</script>